import { Injectable } from '@angular/core';
import {
  createStore, select, setProp, withProps
} from '@ngneat/elf';
import { persistState, localStorageStrategy } from '@ngneat/elf-persist-state';
import { format } from 'date-fns';
import { DateTime } from 'luxon';
import {
  auditTime, BehaviorSubject, catchError, of, shareReplay, tap
} from 'rxjs';

import { DirectScheduleSession, DirectScheduleSessionHttpService } from '@sondermind/data-access/direct-scheduling';

import { SentryClient } from '@sondermind/sentry-client';
import { ITherapistTemplate, ProviderMatchStatus } from '@sondermind/utilities/models-therapist';
import { ClientUser } from '@sondermind/utilities/models-user-client';
import { BreakpointsService } from '@sondermind/utilities/services';

import { AppUiStore } from './app-ui.store';

export const DEFAULT_REQUEST: PatRequest = {
  selectedDate: new Date(),
  type: 'video',
  displayStartTime: null,
  displayEndTime: null,
  startTimeRequest: null,
  endTimeRequest: null,
  sessionIndex: null,
  saveSession: false,
  goalsResponse: '',
  otherResponse: '',
  dayList: []
};

const store = createStore(
  { name: 'proposeATime' },
  withProps<{ proposeATimeClicked: boolean; }>({ proposeATimeClicked: false }),
  withProps<{ closeProgressSheet: boolean; }>({ closeProgressSheet: false }),
  withProps<{ requestFailed: boolean; }>({ requestFailed: false }),
  withProps<{ doppedOutOfPAT: boolean; }>({ doppedOutOfPAT: false }),
  withProps<{ selectedSession: PatRequest; }>({ selectedSession: DEFAULT_REQUEST }),
  withProps<{ currentStep: number; }>({ currentStep: 0 }),
  withProps<{ requestSubmitted: boolean; }>({ requestSubmitted: false }),
  withProps<{ hideNext: boolean; }>({ hideNext: false }),
  withProps<{ hideBack: boolean; }>({ hideBack: false }),
  withProps<{ basicInfoSubmitted: boolean; }>({ basicInfoSubmitted: false }),
  withProps<{ therapist: ITherapistTemplate; }>({ therapist: { locations: [] } as unknown as ITherapistTemplate }),
  withProps<{ availableSessionsInWeek: DirectScheduleSession[]; }>({ availableSessionsInWeek: [] }),
);

persistState(store, {
  key: 'proposeATime',
  storage: localStorageStrategy
});

export interface PatRequest {
  clientReferralMemberId?: number;
  selectedDate: Date;
  type: string;
  displayStartTime: string | null;
  displayEndTime: string | null;
  startTimeRequest: DateTime | null;
  endTimeRequest: DateTime | null;
  sessionIndex: number | null;
  saveSession: boolean;
  goalsResponse: string;
  otherResponse: string;
  isSubmittedByAdmin?: boolean;
  dayList: Array<{
    date: Date;
    noAvailability: boolean;
    month: string;
    dayOfWeek: string;
    dayOfMonth: string;
  }>;
}

interface AvailabilityHistory {
  weekly_id: number;
  days: { [dateKey: string]: number; };
}

interface IApiErrorResponse {
  base: string[];
}

@Injectable({
  providedIn: 'root'
})

export class ProposeATimeStore {
  proposeATimeClicked$ = store.pipe(
    select(({ proposeATimeClicked }) => proposeATimeClicked),
    shareReplay({ refCount: true })
  );
  closeProgressSheet$ = store.pipe(
    select(({ closeProgressSheet }) => closeProgressSheet),
    shareReplay({ refCount: true })
  );
  requestFailed$ = store.pipe(
    select(({ requestFailed }) => requestFailed),
    shareReplay({ refCount: true })
  );
  doppedOutOfPAT$ = store.pipe(
    select(({ doppedOutOfPAT }) => doppedOutOfPAT),
    shareReplay({ refCount: true })
  );
  therapist$ = store.pipe(select(({ therapist }) => therapist));
  availableSessionsInWeek$ = store.pipe(select(({ availableSessionsInWeek }) => availableSessionsInWeek));
  requestSubmitted$ = store.pipe(
    select(({ requestSubmitted }) => requestSubmitted),
    shareReplay({ refCount: true })
  );
  hideNextButton$ = store.pipe(select(({ hideNext }) => hideNext), auditTime(0), shareReplay({ refCount: true }));
  hideBackButton$ = store.pipe(select(({ hideBack }) => hideBack), auditTime(0), shareReplay({ refCount: true }));
  basicInfoSubmitted$ = store.pipe(
    select(({ basicInfoSubmitted }) => basicInfoSubmitted),
    auditTime(0),
    shareReplay({ refCount: true })
  );

  selectedSessionSubj = new BehaviorSubject<PatRequest>(DEFAULT_REQUEST);
  selectedSession$ = this.selectedSessionSubj.asObservable().pipe(auditTime(0));
  availabilityHistorySubject = new BehaviorSubject<{ [weekKey: string]: AvailabilityHistory; }>({});
  availabilityHistory$ = this.availabilityHistorySubject.asObservable();
  inPATAccountSetupCompleteSubj = new BehaviorSubject<boolean>(false);
  inPATAccountSetupComplete$ = this.inPATAccountSetupCompleteSubj.asObservable().pipe(auditTime(0));
  currentStepSubj = new BehaviorSubject<number>(0);
  currentStep$ = this.currentStepSubj.asObservable().pipe(auditTime(0));
  submittingRequest: boolean = false;
  showNoTimesWorkTemplate: boolean = false;
  errorReturned: boolean = false;
  pendoCloseButtonId: string = 'pendo-propose-a-time-date-and-time-close';
  directSchedulingError$ = new BehaviorSubject<string>('');

  readonly enteredByAdminText = '(Client scheduled over the phone by a SonderMind Care Coordinator)';

  constructor(
    private appUiStore: AppUiStore,
    private breakpointService: BreakpointsService,
    private directSchedulingService: DirectScheduleSessionHttpService
  ) {
    this.proposeATimeClicked$.subscribe((val) => {
      if (val) this.goFullScreen(true);
    });
  }

  get store() {
    return store;
  }

  openProgressSheet(therapist: ITherapistTemplate): void {
    this.store.reset();
    this.store.update(setProp('therapist', therapist));
    this.store.update(setProp('proposeATimeClicked', true));
    this.setSelectedSession(DEFAULT_REQUEST);
    this.setCurrentStep(0);
    this.breakpointService.toggleModal(true);
  }

  closeProgressSheet(submittedPAT: boolean = false): void {
    this.setHideNextButton(false);
    this.showNoTimesWorkTemplate = false;
    this.errorReturned = false;
    this.updateInPATAccountSetupComplete(false);
    this.goFullScreen(false);
    this.store.update(setProp('closeProgressSheet', true));
    this.store.update(setProp('doppedOutOfPAT', !submittedPAT));
    this.store.update(setProp('proposeATimeClicked', false));
    this.availabilityHistorySubject.next({});
    this.store.reset();
    this.clearLocalStorage();
    this.breakpointService.toggleModal(false);
  }

  private goFullScreen(value: boolean): void {
    this.setLocalStorageInState();
    this.appUiStore.removeNavOffsetSubj.next(value);
    this.appUiStore.fullscreenSubj.next(value);
    this.appUiStore.appscreenSubj.next(value);
    this.appUiStore.addMainContainerSpacingSubj.next(!value);
  }

  setSelectedSession(updatedValues: Partial<PatRequest>): void {
    const currentSession = this.getSelectedSession();
    const updatedSession: PatRequest = {
      ...currentSession,
      ...updatedValues,
    };
    this.selectedSessionSubj.next(updatedSession);
    localStorage.setItem('proposeATimeSession', JSON.stringify(updatedSession));
  }

  getSelectedSession(): PatRequest {
    return this.selectedSessionSubj.getValue();
  }

  setAvailableSessionsInWeek(sessions: DirectScheduleSession[]): void {
    this.store.update(setProp('availableSessionsInWeek', sessions));
  }

  setRequestFailed(value: boolean): void {
    this.store.update(setProp('requestFailed', value));
  }

  submitPATRequest(user: ClientUser): void {
    this.submittingRequest = true;
    this.setHideNextButton(true);
    this.setHideBackButton(true);
    const provider = this.store.getValue().therapist;
    const request = this.getSelectedSession();
    const defaultType = request.type === 'in_person' ? 'officeSessionDefault' : 'videoSessionDefault';
    const filteredLocations = provider.locations
      .filter((loc) => loc[defaultType] && loc.state === user.profile.mailingAddress.state);

    if (provider.matches?.length > 0) {
      const clientReferralMemberId = provider.matches.filter(
        (x) => !(x.status === ProviderMatchStatus.EXPIRED || x.status === ProviderMatchStatus.DECLINED)
      )[0]?.client_referral_member_id;

      request.clientReferralMemberId = clientReferralMemberId;
    }

    // checking for edge case if user refreshes browser before submitting request
    if (typeof request.startTimeRequest === 'string') {
      request.startTimeRequest = DateTime.fromISO(request.startTimeRequest).setZone(user.profile.timeZone);
    }

    if (typeof request.endTimeRequest === 'string') {
      request.endTimeRequest = DateTime.fromISO(request.endTimeRequest).setZone(user.profile.timeZone);
    }

    const locationId = filteredLocations[0]?.locationId;

    if (locationId == null) {
      this.setRequestFailed(true);
      this.setHideNextButton(true);
      const msg = 'Location not found while proposing a time';
      SentryClient.captureMessage(
        msg,
        {
          tags: {
            persona_provider_id: provider.personaProviderId,
            session_type: request.type
          }
        }
      );
      this.directSchedulingError$.next(msg);
    } else {
      let { goalsResponse, otherResponse } = request;
      if (request.isSubmittedByAdmin) {
        if (goalsResponse) {
          goalsResponse += `\n${this.enteredByAdminText}`;
        }
        if (otherResponse) {
          otherResponse += `\n${this.enteredByAdminText}`;
        }
      }

      this.directSchedulingService.submitDirectScheduleSession({
        locationId,
        personaProviderId: provider.personaProviderId,
        startTime: request.startTimeRequest,
        endTime: request.endTimeRequest,
        sessionType: request.type,
        clientGoals: goalsResponse,
        clientAbout: otherResponse,
        timeProposal: true,
        personaClientId: user.personaId,
        clientReferralMemberId: request.clientReferralMemberId
      }).pipe(
        tap(() => {
          this.closeProgressSheet(true);
          this.store.update(setProp('requestSubmitted', true));
          this.submittingRequest = false;
        }),
        catchError((err: IApiErrorResponse) => {
          const indicator = request.isSubmittedByAdmin ? 'the' : 'your';
          const timeSlotError = err?.base.find(
            (res: string) => res.includes('That time slot is already scheduled. Please try another time slot.')
          );
          if (timeSlotError) {
            this.directSchedulingError$.next(
              `This time has already been taken, please select a different time for ${indicator} session.`
            );
          } else {
            let msg = `There was an issue submitting ${indicator} request for a session.`;
            if (!request.isSubmittedByAdmin) {
              msg += ` Send ${provider.displayFirstName} a message to schedule with them directly.`;
            }
            this.directSchedulingError$.next(msg);
          }
          this.setRequestFailed(true);
          this.setHideNextButton(true);
          return of(null);
        })
      ).subscribe(() => {
        this.submittingRequest = false;
      });
    }
  }

  saveAvailabilityHistory(
    weekKey: string,
    weeklyId: number,
    firstAvailableDay: Date,
    dailyId: number
  ): void {
    const currentHistory = this.availabilityHistorySubject.getValue();
    // Ensure we have an object for the week, with a weekly_id and an empty days object if it's a new week
    const weekHistory = currentHistory[weekKey] || { weekly_id: weeklyId, days: {} };
    // Use the actual first available day as the key for the days object
    const dayKey = format(firstAvailableDay, 'yyyyMMdd');
    weekHistory.days[dayKey] = dailyId;
    // Update the current history with the new or updated week history
    currentHistory[weekKey] = weekHistory;
    // Emit the updated history
    this.availabilityHistorySubject.next(currentHistory);
  }

  getDailyAvailabilityId(weekKey: string, dateKey: string): number | undefined {
    const currentHistory = this.availabilityHistorySubject.getValue();
    return currentHistory[weekKey]?.days[dateKey];
  }

  getWeeklyAvailabilityId(weekKey: string): number | undefined {
    const currentHistory = this.availabilityHistorySubject.getValue();
    return currentHistory[weekKey]?.weekly_id;
  }

  setCurrentStep(step: number): void {
    this.currentStepSubj.next(step);
    localStorage.setItem('proposeATimeStep', step.toString());
  }

  getCurrentStep(): number {
    return this.currentStepSubj.getValue();
  }

  setHideNextButton(value: boolean): void {
    this.store.update(setProp('hideNext', value));
  }

  setHideBackButton(value: boolean): void {
    this.store.update(setProp('hideBack', value));
  }

  setRequestSubmitted(value: boolean): void {
    this.store.update(setProp('requestSubmitted', value));
  }

  setBasicInfoSubmitted(value: boolean): void {
    this.store.update(setProp('basicInfoSubmitted', value));
  }

  updateInPATAccountSetupComplete(value: boolean): void {
    this.inPATAccountSetupCompleteSubj.next(value);
  }

  updatePendoCloseButtonId(value: string): void {
    setTimeout(() => { this.pendoCloseButtonId = value });
  }

  setLocalStorageInState(): void {
    const storedSession = localStorage.getItem('proposeATimeSession');
    const storedStep = localStorage.getItem('proposeATimeStep');
    storedSession ? this.setSelectedSession(JSON.parse(storedSession)) : this.setSelectedSession(DEFAULT_REQUEST);
    storedStep ? this.setCurrentStep(parseFloat(storedStep)) : this.setCurrentStep(0);
  }

  // remove localStorage when progress sheet closed or when idle time logout
  clearLocalStorage(): void {
    localStorage.removeItem('proposeATime');
    localStorage.removeItem('proposeATimeSession');
    localStorage.removeItem('proposeATimeStep');
  }
}
